/**
 * Sparkline — tiny inline trend line, matching the prototype's KPI card
 * sparklines.
 *
 * IMPORTANT DATA-HONESTY NOTE: the prototype's KPI sparklines imply a
 * multi-day historical trend (e.g. last 7 days of a metric). None of the
 * existing APIs this app calls (/jobs, /candidates, /account/users,
 * /clients) return historical/time-series snapshots — only current-state
 * records with a createdAt timestamp. There is no reliable way to
 * reconstruct "what was the open-position count on each of the last 7
 * days" from current-state data alone without risking a fabricated/
 * misleading chart.
 *
 * Per the instruction "If a metric exists in the prototype but there is no
 * corresponding backend/API data, do NOT invent data" — this component
 * therefore renders NOTHING (returns null) when no `data` array is passed,
 * rather than generating placeholder/random values. It only draws a real
 * line when the caller has genuine historical numbers to give it. Today,
 * no page passes real historical data in, so sparklines are visually
 * absent from the rebuilt Dashboard — this is a known, honestly-reported
 * gap (see the audit/report), not a bug.
 *
 * Props:
 *   data  - optional array of numbers, oldest first. Omit/undefined = render nothing.
 *   color - stroke color (default currentColor)
 *   width / height - px dimensions (defaults sized for a KPI card corner)
 */
export default function Sparkline({
  data,
  color = "currentColor",
  width = 64,
  height = 24,
}) {
  if (!data || data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);

  const points = data
    .map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
